import React, { useEffect, useState, useRef } from "react"
import { connect } from "react-redux"
import { Row, Col, Card, CardBody, Table, Button, Badge, Input, Label } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { useNavigate } from "react-router-dom"
import PaymentApi from "../../../Api/paymentApi"
import { Toast } from "primereact/toast"
import { OverlayPanel } from "primereact/overlaypanel"
import { StandardPagination, DocumentViewer } from "components/Common"
import "./Tickets.scss"

const Tickets = props => {
    document.title = "Payment Tickets | Pixwik - Admin"
    const navigate = useNavigate()
    const accessToken = localStorage.getItem("accessToken")
    const toast = useRef(null)
    const statusFilterRef = useRef(null)
    const priorityFilterRef = useRef(null)

    const [loading, setLoading] = useState(false)
    const [updatingTickets, setUpdatingTickets] = useState(new Set())
    const [ticketData, setTicketData] = useState([])
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalTickets: 0,
        hasNextPage: false,
        hasPrevPage: false
    })
    const [error, setError] = useState(null)
    const [documentViewer, setDocumentViewer] = useState({
        isOpen: false,
        url: "",
        title: "",
        type: "image"
    })

    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "Payments", link: "#" },
        { label: "Tickets", link: "#" },
    ]

    const handleBreadcrumbClick = link => {
        if (link !== "#") {
            navigate(link)
        }
    }

    useEffect(() => {
        props.setBreadcrumbItems("Payment Tickets", breadcrumbItems)
        fetchTicketHistory()
    }, [])

    // Fetch ticket history from API
    const fetchTicketHistory = async (page = 1) => {
        try {
            setLoading(true)
            setError(null)
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                params: {
                    page: page,
                    limit: 10
                }
            }

            const response = await PaymentApi.getTicketHistory(config)

            if (response.data.status) {
                setTicketData(response.data.data.paymentTickeHistory || [])
                setPagination(response.data.data.pagination || {
                    currentPage: 1,
                    totalPages: 1,
                    totalTickets: 0,
                    hasNextPage: false,
                    hasPrevPage: false
                })
            }
        } catch (error) {
            setError("Failed to fetch ticket history")
        } finally {
            setLoading(false)
        }
    }

    // Calculate ticket stats from data
    const getTicketStats = () => {
        const openTickets = ticketData.filter(ticket => ticket.status === "open").length
        const closedTickets = ticketData.filter(ticket => ticket.status === "closed").length
        const inProgressTickets = ticketData.filter(ticket => ticket.status === "in_progress").length
        const totalTickets = ticketData.length

        return [
            {
                title: "Open Tickets",
                iconClass: "ion-ios-alert",
                color: "danger",
                total: openTickets,
                subtitle: "Pending Resolution"
            },
            {
                title: "Closed Tickets",
                iconClass: "ion-ios-checkmark-circle",
                color: "success",
                total: closedTickets,
                subtitle: "Resolved"
            },
            {
                title: "In Progress",
                iconClass: "ion-ios-time",
                color: "warning",
                total: inProgressTickets,
                subtitle: "Being Processed"
            },
            {
                title: "Total Tickets",
                iconClass: "ion-ios-list",
                color: "info",
                total: pagination.totalTickets || totalTickets,
                subtitle: "All Time"
            },
        ]
    }

    const [filteredTickets, setFilteredTickets] = useState([])
    const [statusFilter, setStatusFilter] = useState("all")
    const [priorityFilter, setPriorityFilter] = useState("all")

    // Filter tickets based on status and priority
    useEffect(() => {
        let filtered = ticketData

        if (statusFilter !== "all") {
            filtered = filtered.filter(ticket => ticket.status === statusFilter)
        }

        if (priorityFilter !== "all") {
            if (priorityFilter === "high") {
                filtered = filtered.filter(ticket => ticket.isUrgent === true)
            } else if (priorityFilter === "normal") {
                filtered = filtered.filter(ticket => ticket.isUrgent === false)
            }
        }

        setFilteredTickets(filtered)
    }, [statusFilter, priorityFilter, ticketData])

    const getStatusBadge = (status) => {
        switch (status) {
            case 'open':
                return 'danger'
            case 'in_progress':
                return 'warning'
            case 'resolved':
                return 'success'
            case 'closed':
                return 'secondary'
            default:
                return 'secondary'
        }
    }



    const handleStatusChange = async (ticketId, currentStatus) => {
        try {
            // Add ticket to updating set
            setUpdatingTickets(prev => new Set(prev).add(ticketId))

            // Toggle status: if open -> close, if closed -> open
            const newStatus = currentStatus === "open" ? "closed" : "open"

            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                }
            }

            const response = await PaymentApi.updateTicketStatus(ticketId, newStatus, config)

            if (response.data.status) {
                // Update the ticket in local state
                setTicketData(prevData =>
                    prevData.map(ticket =>
                        ticket._id === ticketId
                            ? { ...ticket, status: newStatus }
                            : ticket
                    )
                )

                // Show success toast notification
                const ticketIdShort = ticketId.slice(-8).toUpperCase()
                const statusText = newStatus === "closed" ? "closed" : "reopened"
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Ticket ${ticketIdShort} has been ${statusText} successfully`,
                    life: 3000,
                })
            } else {
                // Handle API response with status false
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to update ticket status. Please try again.",
                    life: 3000,
                })
            }
        } catch (error) {

            // Show error toast notification
            const errorMessage = error.response?.data?.message || "Failed to update ticket status. Please try again."
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: errorMessage,
                life: 4000,
            })
        } finally {
            // Remove ticket from updating set
            setUpdatingTickets(prev => {
                const newSet = new Set(prev)
                newSet.delete(ticketId)
                return newSet
            })
        }
    }

    const handleViewDetails = (ticketId) => {
        // Placeholder for view details functionality
        // TODO: Implement ticket details modal
    }

    const handleViewDocument = (url, title, type = "image") => {
        setDocumentViewer({
            isOpen: true,
            url,
            title,
            type
        })
    }

    const closeDocumentViewer = () => {
        setDocumentViewer({
            isOpen: false,
            url: "",
            title: "",
            type: "image"
        })
    }

    const getFileTypeFromUrl = (url) => {
        if (!url) return 'image'
        const fileExtension = url.split('.').pop().toLowerCase()
        if (fileExtension === 'pdf') return 'pdf'
        if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileExtension)) return 'office'
        return 'image'
    }

    return (
        <>
            <Toast ref={toast} position="top-right" />
            <Row>
                <Col xs="12">
                    <div className="ticket-header d-flex align-items-center justify-content-between">
                        <CustomBreadCrumb
                            data={breadcrumbItems}
                            pageTitle="Payment Tickets"
                            handleClick={handleBreadcrumbClick}
                        />
                    </div>

                    {/* Ticket Stats Cards */}
                    <Row className="mb-4">
                        {getTicketStats().map((stat, key) => (
                            <Col xl={3} sm={6} key={key}>
                                <Card className={`mini-stat bg-${stat.color} ticket-card`}>
                                    <CardBody className="card-body mini-stat-img d-flex align-items-center gap-3">
                                        <div className="mini-stat-icon">
                                            <i className={`ion ${stat.iconClass}`}></i>
                                        </div>
                                        <div className="text-white">
                                            <h6 className="text-uppercase font-size-16 text-white">
                                                {stat.title}
                                            </h6>
                                            <h2 className="text-white">
                                                {stat.total.toLocaleString()}
                                            </h2>
                                            <small className="text-white-50">
                                                {stat.subtitle}
                                            </small>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>



                    {/* Tickets Table */}
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <h4 className="card-title">Payment Issue Tickets</h4>
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => fetchTicketHistory()}
                                    disabled={loading}
                                >
                                    <i className="mdi mdi-refresh me-1"></i>
                                    {loading ? "Loading..." : "Refresh"}
                                </Button>
                            </div>

                            <div className="data-table-container">
                                <div className="table-responsive">
                                    <Table className="table mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Ticket ID</th>
                                            <th>User ID</th>
                                            <th>Issue Type</th>
                                            <th>Payment ID</th>
                                            <th>Description</th>
                                            <th>
                                                <span className="d-flex align-items-center gap-2 justify-content-center">
                                                    <span>Priority</span>
                                                    <span
                                                        onClick={e => priorityFilterRef.current.toggle(e)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="mdi mdi-filter" />
                                                    </span>
                                                </span>
                                                <OverlayPanel ref={priorityFilterRef}>
                                                    <Input
                                                        type="select"
                                                        value={priorityFilter}
                                                        onChange={e => {
                                                            setPriorityFilter(e.target.value)
                                                            priorityFilterRef.current.hide()
                                                        }}
                                                        style={{ width: "150px" }}
                                                    >
                                                        <option value="all">All Priority</option>
                                                        <option value="high">Urgent</option>
                                                        <option value="normal">Normal</option>
                                                    </Input>
                                                </OverlayPanel>
                                            </th>
                                            <th>
                                                <span className="d-flex align-items-center gap-2 justify-content-center">
                                                    <span>Status</span>
                                                    <span
                                                        onClick={e => statusFilterRef.current.toggle(e)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="mdi mdi-filter" />
                                                    </span>
                                                </span>
                                                <OverlayPanel ref={statusFilterRef}>
                                                    <Input
                                                        type="select"
                                                        value={statusFilter}
                                                        onChange={e => {
                                                            setStatusFilter(e.target.value)
                                                            statusFilterRef.current.hide()
                                                        }}
                                                        style={{ width: "150px" }}
                                                    >
                                                        <option value="all">All Status</option>
                                                        <option value="open">Open</option>
                                                        <option value="in_progress">In Progress</option>
                                                        <option value="resolved">Resolved</option>
                                                        <option value="closed">Closed</option>
                                                    </Input>
                                                </OverlayPanel>
                                            </th>
                                            <th>Created Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="9" className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="sr-only">Loading...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : filteredTickets.map((ticket) => (
                                            <tr key={ticket._id} className="ticket-row">
                                                <td>
                                                    <span className="ticket-id fw-bold">
                                                        {ticket._id.slice(-8).toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="user-id">
                                                        {ticket.userId}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="issue-type">
                                                        {ticket.issueType}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="payment-id">
                                                        {ticket.paymentId.slice(-8).toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="description" title={ticket.decription}>
                                                        {ticket.decription.length > 50
                                                            ? ticket.decription.substring(0, 50) + "..."
                                                            : ticket.decription}
                                                    </span>
                                                </td>
                                                <td>
                                                    <Badge color={ticket.isUrgent ? "danger" : "info"}>
                                                        {ticket.isUrgent ? "Urgent" : "Normal"}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Badge color={getStatusBadge(ticket.status)}>
                                                        {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <span className="date">
                                                        {new Date(ticket.createdAt).toLocaleDateString()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        {ticket.attachmenturl && (
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                onClick={() => handleViewDocument(
                                                                    ticket.attachmenturl, 
                                                                    `Ticket ${ticket._id.slice(-8)} - Attachment`,
                                                                    getFileTypeFromUrl(ticket.attachmenturl)
                                                                )}
                                                                className="attachment-btn"
                                                            >
                                                                <i className="mdi mdi-eye me-1"></i>
                                                                View File
                                                            </Button>
                                                        )}
                                                        <Button
                                                            color={ticket.status === "open" ? "success" : "warning"}
                                                            size="sm"
                                                            onClick={() => handleStatusChange(ticket._id, ticket.status)}
                                                            disabled={updatingTickets.has(ticket._id)}
                                                            className="status-btn"
                                                        >
                                                            {updatingTickets.has(ticket._id) ? (
                                                                <>
                                                                    <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                                                                    Updating...
                                                                </>
                                                            ) : ticket.status === "open" ? (
                                                                <>
                                                                    <i className="mdi mdi-check me-1"></i>
                                                                    Close
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="mdi mdi-refresh me-1"></i>
                                                                    Reopen
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    </Table>
                                </div>
                            </div>

                            {error && (
                                <div className="text-center py-4">
                                    <div className="text-danger">
                                        <i className="mdi mdi-alert-circle display-4 d-block mb-2"></i>
                                        {error}
                                        <br />
                                        <Button color="primary" size="sm" onClick={() => fetchTicketHistory()} className="mt-2">
                                            Retry
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {!loading && !error && filteredTickets.length === 0 && (
                                <div className="text-center py-4">
                                    <div className="text-muted">
                                        <i className="mdi mdi-ticket-outline display-4 d-block mb-2"></i>
                                        No tickets found matching your filters
                                    </div>
                                </div>
                            )}

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <StandardPagination
                                    currentPage={pagination.currentPage}
                                    totalPages={pagination.totalPages}
                                    onPageChange={(page) => fetchTicketHistory(page)}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Document Viewer Modal */}
            <DocumentViewer
                isOpen={documentViewer.isOpen}
                toggle={closeDocumentViewer}
                documentUrl={documentViewer.url}
                documentTitle={documentViewer.title}
                documentType={documentViewer.type}
            />
        </>
    )
}

export default connect(null, { setBreadcrumbItems })(Tickets)